import { useEffect, useMemo } from 'react';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import {
  applyOptimisticPositionStructure,
  positionStructureIntentMatchesModel,
} from '../services/applyOptimisticPositionStructure';
import { usePositionStructureOptimisticStore } from '../stores/positionStructureOptimisticStore';

/**
 * Keeps every Position Chart surface on the same disposable DB-owned preview.
 * The projection stays referentially stable until its canonical model or
 * optimistic operation changes.
 */
export function useOptimisticPositionStructureModel(
  folderId: string | null | undefined,
  canonicalModel: OrgModelProjection | undefined,
): OrgModelProjection | undefined {
  const operation = usePositionStructureOptimisticStore(
    (state) => folderId ? state.operationsByFolder.get(folderId) ?? null : null,
  );
  const canonicalMatches = Boolean(
    canonicalModel
    && operation
    && operation.folderId === folderId
    && operation.expectedGraphVersion !== null
    && canonicalModel.graphVersion >= operation.expectedGraphVersion
    && positionStructureIntentMatchesModel(canonicalModel, operation.intent),
  );

  useEffect(() => {
    if (canonicalMatches && operation) {
      usePositionStructureOptimisticStore.getState().clear(
        operation.folderId,
        operation.intent.intentId,
      );
    }
  }, [canonicalMatches, operation]);

  return useMemo(() => {
    return canonicalModel
      && operation
      && operation.folderId === folderId
      && !canonicalMatches
      ? applyOptimisticPositionStructure(canonicalModel, operation.intent)
      : canonicalModel;
  }, [canonicalMatches, canonicalModel, folderId, operation]);
}
